import { Box, Button, Input, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { FC, useEffect, useState } from "react";
import { DetailsUI, ProfileUI } from "../BoxUI";
import useResponsive from "../../hooks/useResponsive";

import {
  formatDate,
  formatFullName,
  formatTextType,
  getUrlPathId,
  limitToSeven,
} from "../../utils/url-id";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
// import EmployeeRequestContent from "./EmployeeRequest";
import { useLocation } from "react-router-dom";

import { useAuth } from "../../hooks/useAuth";
// import { apiGetMobilityScoreByEmployeeId } from "../../api/dashboard";
import { useNotificationService } from "../notification";
import { UsersDataProps } from "../../interface/users";
import { labelSty, labelStyles, textSty } from "../UIElements/styles-input";
import { useMutation } from "@tanstack/react-query";
import { apiAssignRolesToUsers, apiGetRoles } from "../../api/access";
import { InputCustom } from "../UIElements/input-ui";
import { SearchSharp } from "@mui/icons-material";

const UsersDetailsContent: FC<{
  userInfo: UsersDataProps;
  handleRefresh: () => void;
  handleDeleteModal: () => void;
  handleSuspendModal: () => void;
}> = ({ userInfo, handleRefresh, handleSuspendModal, handleDeleteModal }) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const location = useLocation();
  const { showNotification } = useNotificationService();

  const [role_list, setRole_list] = React.useState([]);
  const [assign_role, setAssign_role] = React.useState(false);

  useEffect(() => {
    mutation.mutate();
  }, []);

  const mutation = useMutation({
    mutationFn: () => apiGetRoles(),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        setRole_list(data.data?.data);
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });
  const assign_mutation = useMutation({
    mutationFn: (field: { userId: string; roleId: string[] }) =>
      apiAssignRolesToUsers(field),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Success");
        handleRefresh();
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });

  const handleOpenModal = () => setAssign_role(!assign_role);

  const headerSty = {
    fontFamily: "Lexend",
    fontWeight: 500,
    my: isMedium ? 2 : 3,
    color: theme.palette.neutral.light,
  };
  const handleRoleAssign = (id: string) => {
    const userId = getUrlPathId(location?.pathname);
    assign_mutation.mutate({ userId: userId, roleId: [id] });
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "white",
          p: 4,
          m: 3,
        }}
      >
        <Box>
          <Typography sx={{ ...headerSty, my: 0 }} variant="subtitle2">
            User Information
          </Typography>
          <Stack
            direction={isMedium ? "column" : "row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={{ my: 3, gap: 2 }}
          >
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <Typography sx={{ ...labelStyles, mb: 0 }}>First Name</Typography>
              <Typography sx={{ ...textSty }}>
                {formatTextType(userInfo?.first_name) ?? "-"}
              </Typography>
            </Stack>
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <Typography sx={{ ...labelStyles, mb: 0 }}>Last Name</Typography>
              <Typography sx={{ ...textSty }}>
                {formatTextType(userInfo?.last_name ?? "-")}
              </Typography>
            </Stack>
          </Stack>
          <Stack sx={{ width: "100%", my: 2 }}>
            <Typography sx={{ ...labelStyles, mb: 0 }}>Middle Name</Typography>
            <Typography sx={{ ...textSty }}>
              {formatTextType(userInfo?.middle_name)}
            </Typography>
          </Stack>

          <Stack
            direction={isMedium ? "column" : "row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={{ my: 3, gap: 2 }}
          >
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <Typography sx={{ ...labelStyles, mb: 0 }}>Gender</Typography>
              <Typography sx={{ ...textSty }}>
                {formatTextType(userInfo?.gender) ?? "-"}
              </Typography>
            </Stack>
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <Typography sx={{ ...labelStyles, mb: 0 }}>Email</Typography>
              <Typography sx={{ ...textSty }}>
                {userInfo?.email ?? "-"}
              </Typography>
            </Stack>
          </Stack>

          <Stack
            direction={isMedium ? "column" : "row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={{ my: 3, gap: 2 }}
          >
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <Typography sx={{ ...labelStyles, mb: 0 }}>
                Phone Number
              </Typography>
              <Typography sx={{ ...textSty }}>
                {userInfo?.phone_number}
              </Typography>
            </Stack>
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <Typography sx={{ ...labelStyles, mb: 0 }}>Role</Typography>
              <Typography sx={{ ...textSty }}>
                {userInfo?.role ?? ""}
              </Typography>
            </Stack>
          </Stack>
        </Box>
        {userInfo?.rolePermissions?.length > 0 && (
          <Stack>
            <Stack>
              <Typography
                variant="body1"
                sx={{ ...headerSty, textAlign: "start" }}
              >
                Role Permissions
              </Typography>
            </Stack>
            {userInfo?.rolePermissions?.map((val, index) => (
              <Stack
                key={index}
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Typography sx={{ ...labelSty }}>
                  {formatTextType(val?.name)}
                </Typography>
                <Button
                  // onClick={() => handlePermissionsDelete(val)}
                  color="error"
                  endIcon={<CancelIcon />}
                  sx={{ width: 100 }}
                >
                  Remove
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: isMedium ? "center" : "flex-end",
          px: 3,
          gap: 4,
        }}
      >
        <Button
          variant="contained"
          color="success"
          onClick={handleOpenModal}
          sx={{ minWidth: isMedium ? 100 : 150, color: "white" }}
        >
          {assign_role ? "Close" : "Assign Role"}
        </Button>
        <Button
          onClick={handleSuspendModal}
          variant="contained"
          color="warning"
          sx={{ minWidth: isMedium ? 100 : 150, color: "white" }}
        >
          Suspend
        </Button>
        <Button
          onClick={handleDeleteModal}
          variant="contained"
          color="error"
          sx={{ minWidth: isMedium ? 100 : 150 }}
        >
          Delete
        </Button>
      </Box>
      {assign_role && (
        <Box sx={{ p: 4, m: 3, background: "white" }}>
          {role_list?.length > 0 && (
            <Stack>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                sx={{ mb: 2 }}
              >
                <Typography sx={{ ...headerSty, textAlign: "start", my: 0 }}>
                  Role List
                </Typography>
                <Box component={"form"}>
                  <InputCustom
                    type="text"
                    placeholder="Search role name.."
                    value=""
                    onchange={() => {}}
                    styles={{
                      width: isMedium ? 100 : 250,
                    }}
                  />
                  <Button
                    size="small"
                    endIcon={<SearchIcon sx={{ width: 20, height: 20 }} />}
                    variant="contained"
                    sx={{ height: "2.1rem", minWidth: 30, borderRadius: 1 }}
                  ></Button>
                </Box>
              </Stack>
              {role_list?.map((val: any, index) => (
                <Stack
                  key={index}
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  sx={{ my: 1 }}
                >
                  <Typography sx={{ ...labelSty, width: "75%" }}>
                    {formatTextType(val?.name)}
                  </Typography>

                  <Button
                    variant="contained"
                    onClick={() => handleRoleAssign(val?._id)}
                    sx={{ width: 80, color: "white" }}
                  >
                    Assign
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </>
  );
};

export default UsersDetailsContent;
